import { useEffect, useMemo, useState } from "react";
import { Inbox, Phone, Briefcase, Lock, LogOut } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { SourceBreakdown, sourceLabel } from "../components/SourceBreakdown";
import { Seo } from "../components/Seo";

const TABS = [
  { key: "leads", label: "Enquiries", icon: Inbox, ep: "/leads" },
  { key: "callbacks", label: "Callbacks", icon: Phone, ep: "/callbacks" },
  { key: "brokers", label: "Brokers", icon: Briefcase, ep: "/brokers" },
];
const TOKEN_KEY = "dna_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [authing, setAuthing] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setAuthing(true);
    try {
      const res = await api.post("/admin/login", { password });
      sessionStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      setPassword("");
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Incorrect password.");
    } finally { setAuthing(false); }
  };

  const logout = () => { sessionStorage.removeItem(TOKEN_KEY); setToken(""); };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="pt-32 min-h-screen">
        <Seo title="Admin" path="/admin" noindex />
        <section className="container-x mx-auto max-w-[460px] py-16">
          <div className="border border-[#808080]/30 p-8 md:p-10">
            <div className="w-14 h-14 bg-lime flex items-center justify-center mb-6"><Lock className="w-7 h-7 text-[#1A1A1A]" /></div>
            <h1 className="text-3xl font-black tracking-tight">Admin access</h1>
            <p className="text-grey mt-2 mb-8">Enter the admin password to view enquiries.</p>
            <form onSubmit={login} className="space-y-4">
              <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="w-full bg-[#111] border border-[#808080]/30 px-4 py-3 text-white outline-none focus:border-lime" />
              <button data-testid="admin-login-btn" disabled={authing} className="w-full bg-lime text-[#1A1A1A] font-bold py-4 hover:glow-lime transition-shadow">{authing ? "Checking..." : "Log In"}</button>
            </form>
          </div>
        </section>
      </div>
    );
  }

  return <><Seo title="Admin" path="/admin" noindex /><Dashboard token={token} onLogout={logout} /></>;
}

function Dashboard({ token, onLogout }) {
  const [tab, setTab] = useState("leads");
  const [data, setData] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [statsRes, ...results] = await Promise.all([api.get("/admin/stats", { headers }), ...TABS.map((t) => api.get(t.ep, { headers }))]);
        const next = {};
        TABS.forEach((t, i) => { next[t.key] = results[i].data; });
        setData(next);
        setStats(statsRes.data);
      } catch (err) {
        if (err.response?.status === 401) { toast.error("Session expired. Please log in again."); onLogout(); }
      } finally { setLoading(false); }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const rows = data[tab] || [];
  const columns = useMemo(() => (rows[0] ? Object.keys(rows[0]).filter((k) => k !== "id") : []), [rows]);

  return (
    <div data-testid="admin-page" className="pt-32 min-h-screen">
      <section className="container-x mx-auto max-w-[1400px] py-12">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Admin Dashboard</h1>
            <p className="text-grey mt-2">Lead generation & enquiry overview</p>
          </div>
          <button data-testid="admin-logout-btn" onClick={onLogout} className="inline-flex items-center gap-2 border border-[#808080]/30 px-5 py-3 text-sm hover:border-lime hover:text-lime transition-colors"><LogOut className="w-4 h-4" /> Log out</button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#808080]/20 border border-[#808080]/20 mt-8">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)} className={`flex items-center gap-3 p-5 transition-colors ${tab === t.key ? "bg-lime text-[#1A1A1A]" : "bg-[#1A1A1A] hover:bg-[#111]"}`}>
              <t.icon className="w-5 h-5" />
              <div className="text-left"><p className="text-2xl font-black">{(data[t.key] || []).length}</p><p className="text-xs uppercase tracking-wide">{t.label}</p></div>
            </button>
          ))}
        </div>

        <SourceBreakdown stats={stats} />

        <div className="mt-8 border border-[#808080]/20 overflow-x-auto">
          {loading && rows.length === 0 ? (
            <p className="p-8 text-grey">Loading...</p>
          ) : rows.length === 0 ? (
            <p className="p-8 text-grey" data-testid="admin-empty">No records yet.</p>
          ) : (
            <table className="w-full text-sm" data-testid="admin-table">
              <thead><tr className="border-b border-[#808080]/20 text-left text-grey uppercase text-xs">
                {columns.map((k) => <th key={k} className="p-4 font-medium whitespace-nowrap">{k.replace(/_/g, " ")}</th>)}
              </tr></thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b border-[#808080]/10 hover:bg-[#111]">
                    {columns.map((k) => (
                      <td key={k} className="p-4 whitespace-nowrap max-w-xs truncate">{k === "source" ? sourceLabel(String(row[k] ?? "")) : String(row[k] ?? "—")}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
